import styled, { css } from 'styled-components';

import PropTypes from 'prop-types';
import closeBtnImg from '../../img/dlg-close.png';
import { useCallback, useEffect, useRef } from 'react';
import { useDialogSliceSelector } from '../../store/modules/dialog';
import ga from '../../utils/gaConnect';
import { DIALOG_TYPE } from '../../define/global';
import { verticalSlideGenerator } from '../common/AnimationCommon';
import Blanket from '../common/Blanket';
import { cssClearFix } from '../common/CssCommon';
import { useAppDataSliceSelector } from '../../store/modules/appData';
import CONSTANT from '../../define/constant';

const ANIMATION_TIME = 300;

const slideUp = verticalSlideGenerator('10%', '0px');
const slideDown = verticalSlideGenerator('0px', '10%');

const ContentContainer = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: fit-content;
  height: fit-content;
  background: white;
  border-radius: 2px;
  /* display: flex; */
  border: 1px solid #b1b6bb;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.4);
  /* padding: 1rem; */
  box-sizing: border-box;
  transition: ${ANIMATION_TIME}ms ease;

  ${({ isAnimatin }) => {
    return (
      isAnimatin &&
      css`
        animation-duration: ${ANIMATION_TIME}ms;
        animation-timing-function: ease-in-out;
        animation-name: ${slideUp};
        animation-fill-mode: forwards;
      `
    );
  }}

  ${({ isClosing, isAnimatin }) =>
    isClosing &&
    isAnimatin &&
    css`
      //transform: scale(0, 0) rotate(360deg);
      animation-name: ${slideDown};
    `}

  ${({ isFullScreen }) =>
    isFullScreen &&
    css`
      width: 100%;
      height: 100%;
    `};

  ${({ cssExt }) => cssExt && cssExt}
`;

const TitleArea = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  ${cssClearFix};
`;

const TitleWrapper = styled.div`
  display: flex;
  height: ${CONSTANT.DIALOG_HEADER_HEIGHT}px;
  justify-content: space-between;
  align-items: center;
  padding: 0 12px;
`;

const Icon = styled.div`
  margin-right: 6px;
  height: 32px;
`;
const Title = styled.div`
  height: fit-content;
  font-size: 15px;
  font-weight: 400;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #2f3133;
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
`;

const CloseBtn = styled.div`
  width: 24px;
  height: 24px;
  object-fit: contain;
  cursor: pointer;
`;

const DialogTitleSeperateLine = styled.div`
  height: 1px;
  background-color: #b1b6bb;
`;

function Dialog({
  icon,
  icon2x,
  title,
  showCloseBtn,
  showTitleSeperateLine,
  children,
  animationWhenClose,
  onClickCloseBtn,
  isShow,
  isFullScreen,
  isBlanketTransparent,
  isNoHeader,
  cssExt
}) {
  const state = useDialogSliceSelector();
  const currentTime = useRef(0);
  const appData = useAppDataSliceSelector();
  //const [animate, setAnimate] = useState(false);
  //const [localVisible, setLocalVisible] = useState(isShow);

  const sendGADialogInfo = useCallback(() => {
    let gaData = undefined;
    switch (state.dialogType) {
      case DIALOG_TYPE.DIALOG_SHARE_LINK_DOCUMENT:
        gaData = { category: '공유', action: '공유메뉴열기' };
        break;
      case DIALOG_TYPE.DIALOG_INSTALL_DOCUMENT:
        gaData = { category: '전체서비스', action: '전체서비스메뉴열기' };
        if (appData.shareId !== '') gaData.label = '공유뷰';
        break;
      case DIALOG_TYPE.DIALOG_INVALID_SHARE_LINK:
        gaData = { category: '공유문서', action: '공유문서만료' };
        break;
      default:
        break;
    }

    if (gaData !== undefined) ga.event(gaData);
  }, [state, appData]);

  const sendGADialogCloseInfo = useCallback(() => {
    const closeTime = new Date() - currentTime.current;
    let gaData = undefined;
    switch (state.dialogType) {
      case DIALOG_TYPE.DIALOG_SHARE_LINK_DOCUMENT:
        gaData = { category: '공유', action: '닫기', label: '공유메뉴', value: closeTime };
        break;
      case DIALOG_TYPE.DIALOG_INSTALL_DOCUMENT:
        gaData = { category: '전체서비스', action: '닫기', value: closeTime };
        if (appData.shareId !== '') gaData.label = '공유뷰';
        break;
      case DIALOG_TYPE.DIALOG_DOWNLOAD_DOCUMENT:
        gaData = { category: '문서 다운로드', action: '닫기', value: closeTime };
        break;
      case DIALOG_TYPE.DIALOG_OPEN_DOCUMENT:
        gaData = { category: '문서열기', action: '닫기', label: '시작화면', value: closeTime };
        break;
      default:
        break;
    }

    if (gaData !== undefined) ga.event(gaData);
  }, [state, currentTime, appData]);

  useEffect(() => {
    sendGADialogInfo();
    currentTime.current = new Date();
  }, [currentTime, sendGADialogInfo]);

  const closeInner = () => {
    sendGADialogCloseInfo();
    if (onClickCloseBtn) onClickCloseBtn();
  };

  return (
    <>
      <Blanket isTransparent={isBlanketTransparent}></Blanket>
      <ContentContainer isClosing={!isShow} isFullScreen={isFullScreen} cssExt={cssExt}>
        {isNoHeader !== true && (
          <TitleArea>
            <TitleWrapper>
              <Title>
                {icon && (
                  <Icon>
                    <img
                      width="32px"
                      height="32px"
                      src={icon}
                      srcSet={icon2x}
                      alt="formatIcon"></img>
                  </Icon>
                )}
                {title}
              </Title>
              {showCloseBtn && (
                <CloseBtn onClick={closeInner}>
                  <img src={closeBtnImg} alt="Close Button"></img>
                </CloseBtn>
              )}
            </TitleWrapper>

            {showTitleSeperateLine && <DialogTitleSeperateLine />}
          </TitleArea>
        )}

        {children}
      </ContentContainer>
    </>
  );
}

Dialog.propTypes = {
  size: PropTypes.oneOf(['x-small', 'small', 'medium', 'large'])
};

export default Dialog;
